import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, InputGroup, Alert, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient, API_ENDPOINTS as ENDPOINTS } from '../utils/apiUtils';
import { useNotification, NotificationMessages, WorkPlanMessages } from '../hooks/useNotification';
import { getStatusVariant } from '../utils/statusUtils';
import { handleApiResponse, API_ENDPOINTS } from '../utils/apiUtils';
import { formatDate } from '../utils/uiUtils';
import { canEditItem, canDeleteItem } from '../utils/permissionUtils';
import WorkPlanModal from './WorkPlanModal';

const Plan = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { notification, showSuccess, showError } = useNotification();
  const [workPlans, setWorkPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [templateLoading, setTemplateLoading] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workPlanToDelete, setWorkPlanToDelete] = useState(null);
  const [showWorkPlanModal, setShowWorkPlanModal] = useState(false);
  const [editingWorkPlan, setEditingWorkPlan] = useState(null);

  // Set up axios default headers
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  useEffect(() => {
    fetchWorkPlans();
  }, []);

  const fetchWorkPlans = async () => {
    setLoading(true);
    const response = await handleApiResponse(
      () => axios.get(API_ENDPOINTS.WORK_PLANS),
      'fetch work plans'
    );
    
    if (response.success) {
      setWorkPlans(response.data);
      setFilteredPlans(response.data);
      setError('');
    } else {
      setError(response.error.message);
    }
    
    setLoading(false);
  };

  // Filter work plans based on search term and status
  useEffect(() => {
    let filtered = workPlans;

    if (searchTerm.trim()) {
      filtered = filtered.filter(plan =>
        plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.created_by_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.created_by_department.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'All') {
      filtered = filtered.filter(plan => plan.status === statusFilter);
    }

    setFilteredPlans(filtered);
  }, [workPlans, searchTerm, statusFilter]);

  // Status variant function moved to utils/statusUtils.js

  const getUniqueStatuses = () => {
    const statuses = [...new Set(workPlans.map(plan => plan.status))];
    return ['All', ...statuses];
  };

  const clearSearch = () => {
    setSearchTerm('');
    setStatusFilter('All');
  };

  const hasActiveSearch = searchTerm.trim() || statusFilter !== 'All';

  // Use centralized permission system
  const canEdit = (plan) => {
    if (!user || !plan) return false;
    return canEditItem(user, plan, 'workplan').canEdit;
  };

  const canDelete = (plan) => {
    if (!user || !plan) return false;
    return canDeleteItem(user, plan).canDelete;
  };

  const handleDeleteClick = (plan) => {
    setWorkPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!workPlanToDelete) return;
    
    try {
      const response = await handleApiResponse(
        () => axios.delete(`${API_ENDPOINTS.WORK_PLANS}/${workPlanToDelete.work_plan_id}`),
        'delete work plan'
      );
      
      if (response.success) {
        setWorkPlans(prev => prev.filter(p => p.work_plan_id !== workPlanToDelete.work_plan_id));
        setFilteredPlans(prev => prev.filter(p => p.work_plan_id !== workPlanToDelete.work_plan_id));
        showSuccess(WorkPlanMessages.WORK_PLAN_DELETED);
      } else {
        showError(response.error.message);
      }
    } catch (e) {
      console.error('Delete work plan failed', e);
      showError(WorkPlanMessages.WORK_PLAN_DELETE_FAILED);
    } finally {
      setShowDeleteModal(false);
      setWorkPlanToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setWorkPlanToDelete(null);
  };

  const handleGenerateTemplate = async (planId) => {
    setTemplateLoading(prev => ({ ...prev, [planId]: true }));
    
    try {
      const templateResponse = await apiClient.post(`${ENDPOINTS.WORK_PLANS}/generate-template`, {
        work_plan_id: planId,
        timestamp: new Date().toISOString()
      });

      // Create the print HTML with the template content and auto-print script
      const html = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Extension Work Plan</title>
          <style>
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; background: #fff; }
            @page { size: A4 landscape; margin: 12mm; }
            #template-container { 
              width: 100%; 
              border: 2px solid #000; 
              background: #fff; 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
              page-break-inside: avoid;
            }
            table { width: 100%; border-collapse: collapse; }
            td, th { border: 1px solid #000; padding: 6px 8px; vertical-align: top; }
            .header-logo { width: 60px; height: 60px; }
            .title { font-weight: bold; text-align: center; font-size: 16px; }
            .month-header { text-align: center; font-size: 10px; }
            .month-cell { text-align: center; width: 30px; }
            .objectives-cell { width: 25%; }
            .output-cell { width: 25%; }
            .activities-cell { width: 25%; }
            .months-cell { width: 25%; }
            .add-rows-note { font-style: italic; font-size: 10px; }
          </style>
        </head>
        <body>
          ${templateResponse.data.htmlContent}
          <script>
            window.onload = function() {
              setTimeout(function(){ window.print(); window.close(); }, 50);
            };
          <\/script>
        </body>
      </html>`;
      
      const printWindow = window.open('', '_blank', 'width=1200,height=900');
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

    } catch (err) {
      console.error('Error generating template:', err);
      showError('Failed to generate template. Please try again.');
    } finally {
      setTemplateLoading(prev => ({ ...prev, [planId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="plan-container">
        <div className="py-4 px-4">
          <div>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading work plans...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="plan-container">
      <div className="py-4 px-4">
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3 className="mb-1">Work Plans</h3>
              </div>
              <Button 
                variant="success" 
                onClick={() => {
                  setEditingWorkPlan(null);
                  setShowWorkPlanModal(true);
                }}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Create a Work Plan
              </Button>
            </div>

            {/* Search and Filter Section */}
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Search Work Plans</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search by title, creator, or department..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Filter by Status</Form.Label>
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        {getUniqueStatuses().map(status => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <Button
                      variant="outline-secondary"
                      onClick={clearSearch}
                      disabled={!hasActiveSearch}
                      className="w-100"
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear
                    </Button>
                  </div>
                </div>
                
                {/* Results Summary */}
                {hasActiveSearch && (
                  <div className="mt-3">
                    <small className="text-muted">
                      Showing {filteredPlans.length} of {workPlans.length} work plans
                      {searchTerm.trim() && ` matching "${searchTerm}"`}
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Work Plans Table */}
            <Card className="shadow-sm">
              <Card.Body>
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                {filteredPlans.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-calendar-check display-1 text-muted mb-3"></i>
                    <h4 className="text-muted">No Work Plans Found</h4>
                    <p className="text-muted">
                      {hasActiveSearch 
                        ? 'No work plans match your search criteria. Try adjusting your filters.'
                        : 'No work plans have been created yet. Create your first work plan to get started.'
                      }
                    </p>
                  </div>
                ) : (
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Created By</th>
                        <th>Status</th>
                        <th>Date Submitted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlans.map((plan) => (
                        <tr key={plan.work_plan_id}>
                          <td>
                            <div>
                              <span>{plan.title}</span>
                            </div>
                          </td>
                          <td>{plan.created_by_name}</td>
                          <td>
                            <Badge bg={getStatusVariant(plan.status)}>
                              {plan.status || 'Pending'}
                            </Badge>
                          </td>
                          <td>{formatDate(plan.created_at)}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-view-${plan.work_plan_id}`}>View</Tooltip>}>
                                <Button as={Link} to={`/plan/${plan.work_plan_id}`} variant="link" className="icon-only-btn icon-view" aria-label="View work plan">
                                  <i className="bi bi-eye"></i>
                                </Button>
                              </OverlayTrigger>

                              {canEdit(plan) && (
                                <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-edit-${plan.work_plan_id}`}>Edit</Tooltip>}>
                                  <Button 
                                    onClick={() => {
                                      setEditingWorkPlan(plan);
                                      setShowWorkPlanModal(true);
                                    }} 
                                    variant="link" 
                                    className="icon-only-btn icon-edit" 
                                    aria-label="Edit work plan"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                </OverlayTrigger>
                              )}


                              {canDelete(plan) && (
                                <OverlayTrigger placement="bottom" overlay={<Tooltip id={`tt-del-${plan.work_plan_id}`}>Delete</Tooltip>}>
                                  <Button onClick={() => handleDeleteClick(plan)} variant="link" className="icon-only-btn icon-delete" aria-label="Delete work plan">
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                </OverlayTrigger>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            Delete Work Plan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-trash-fill text-danger" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Confirm Deletion</h5>
            <p className="text-muted mb-3">
              Are you sure you want to delete this work plan? This action cannot be undone.
            </p>
            {workPlanToDelete && (
              <div className="bg-light p-3 rounded">
                <strong>{workPlanToDelete.title}</strong>
                <div className="text-muted mt-1">
                  <small>
                    <i className="bi bi-person-fill me-1"></i>
                    Created by: {workPlanToDelete.created_by_name}
                  </small>
                </div>
                <div className="text-muted">
                  <small>
                    <i className="bi bi-calendar-event me-1"></i>
                    Created: {formatDate(workPlanToDelete.created_at)}
                  </small>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleDeleteCancel}>
            <i className="bi bi-x-lg me-1"></i>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            <i className="bi bi-trash-fill me-1"></i>
            Delete Work Plan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Work Plan Modal */}
      <WorkPlanModal
        show={showWorkPlanModal}
        onHide={() => {
          setShowWorkPlanModal(false);
          setEditingWorkPlan(null);
        }}
        workPlan={editingWorkPlan}
        onSuccess={() => {
          fetchWorkPlans();
          showSuccess(editingWorkPlan ? WorkPlanMessages.WORK_PLAN_UPDATED : WorkPlanMessages.WORK_PLAN_CREATED);
        }}
      />
    </div>
  );
};

export default Plan;
